import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import { InteractionStatus, type AccountInfo } from '@azure/msal-browser'
import * as Sentry from '@sentry/react'
import { useNavigate } from 'react-router-dom'
import { loginRequest } from '@/lib/auth-config'
import { identifyUser, resetAnalytics } from '@/lib/analytics'
import { audit } from '@/lib/audit'

const FRIDAY_TOKEN_KEY = 'friday_token'

function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

function isTokenExpired(token: string): boolean {
  const expiry = getTokenExpiry(token)
  return expiry === null || expiry <= Date.now()
}

interface AuthUser {
  email: string
  displayName: string
  userId: string
}

interface AuthContextValue {
  user: AuthUser | null
  fridayToken: string | null
  isLoading: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  extendSession: () => Promise<void>
  handleUnauthorized: () => Promise<void>
  getTokenExpiry: (token: string) => number | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

// Module-level unauthorised handler so the API client (Step 3) can call it
// without a circular import.
let _unauthorizedHandler: (() => Promise<void>) | null = null
export function getUnauthorizedHandler() {
  return _unauthorizedHandler
}

function accountToUser(account: AccountInfo): AuthUser {
  return {
    email: account.username,
    displayName: account.name ?? account.username,
    userId: account.homeAccountId,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const navigate = useNavigate()

  const [user, setUser] = useState<AuthUser | null>(null)
  const [fridayToken, setFridayToken] = useState<string | null>(() => {
    const stored = sessionStorage.getItem(FRIDAY_TOKEN_KEY)
    if (!stored) return null
    if (isTokenExpired(stored)) {
      sessionStorage.removeItem(FRIDAY_TOKEN_KEY)
      return null
    }
    return stored
  })
  const [isLoading, setIsLoading] = useState(true)
  const initRef = useRef(false)

  const exchangeToken = useCallback(
    async (accessToken: string): Promise<void> => {
      const apiBase = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787'
      const response = await fetch(
        `${apiBase}/auth/sso/m365`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ access_token: accessToken }),
        },
      )
      if (!response.ok) throw new Error(`Token exchange failed: ${response.status}`)
      const { token } = (await response.json()) as { token: string }
      sessionStorage.setItem(FRIDAY_TOKEN_KEY, token)
      setFridayToken(token)
    },
    [],
  )

  const clearAuth = useCallback(() => {
    sessionStorage.removeItem(FRIDAY_TOKEN_KEY)
    setFridayToken(null)
    setUser(null)
  }, [])

  const handleUnauthorized = useCallback(async () => {
    const accounts = instance.getAllAccounts()
    if (accounts.length > 0) {
      try {
        const result = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        })
        await exchangeToken(result.accessToken)
        return
      } catch {
        // Silent re-auth failed — fall through to logout
      }
    }
    Sentry.captureMessage('Auth token rejected', 'warning')
    clearAuth()
    navigate('/login', { replace: true })
  }, [instance, exchangeToken, clearAuth, navigate])

  // Register handler for use by the API client
  useEffect(() => {
    _unauthorizedHandler = handleUnauthorized
    return () => {
      _unauthorizedHandler = null
    }
  }, [handleUnauthorized])

  // Initialise auth state on mount / after MSAL redirect completes
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return
    if (initRef.current) return
    initRef.current = true

    const init = async () => {
      try {
        const accounts = instance.getAllAccounts()
        if (accounts.length === 0) return

        const account = accounts[0]
        setUser(accountToUser(account))

        // Identify the user in PostHog now that auth is confirmed
        identifyUser(account.username, account.name ?? account.username)

        // If we have a valid stored FRIDAY JWT, reuse it
        const stored = sessionStorage.getItem(FRIDAY_TOKEN_KEY)
        if (stored && !isTokenExpired(stored)) {
          setFridayToken(stored)
          audit({ action: 'login_success' })
          return
        }

        // Silently get a fresh M365 token and re-exchange
        const result = await instance.acquireTokenSilent({
          ...loginRequest,
          account,
        })
        await exchangeToken(result.accessToken)
        audit({ action: 'login_success' })
      } catch (err) {
        clearAuth()
        Sentry.captureException(err, { tags: { context: 'auth_init' } })
        audit({ action: 'login_failure' })
      } finally {
        setIsLoading(false)
      }
    }

    if (isAuthenticated) {
      init()
    } else {
      setIsLoading(false)
    }
  }, [instance, inProgress, isAuthenticated, exchangeToken, clearAuth])

  const login = useCallback(async () => {
    await instance.loginRedirect(loginRequest)
  }, [instance])

  const logout = useCallback(async () => {
    audit({ action: 'logout' })
    resetAnalytics()
    clearAuth()
    await instance.logoutRedirect({ postLogoutRedirectUri: '/login' })
  }, [instance, clearAuth])

  const extendSession = useCallback(async () => {
    const accounts = instance.getAllAccounts()
    if (accounts.length === 0) { await logout(); return }
    try {
      const result = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      })
      await exchangeToken(result.accessToken)
    } catch {
      await logout()
    }
  }, [instance, exchangeToken, logout])

  return (
    <AuthContext.Provider
      value={{
        user,
        fridayToken,
        isLoading,
        login,
        logout,
        extendSession,
        handleUnauthorized,
        getTokenExpiry,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
